import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import React, { Suspense, useState } from 'react'
import './App.css'
import type { SelectTabData, SelectTabEvent } from '@fluentui/react-components'
import { Spinner, Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, Button } from '@fluentui/react-components'
import ParticlesBackground from './components/ParticlesBackground'
import { FoldableSection } from './components/FoldableSection'
import ScrollReveal from './components/ScrollReveal'
import { NavigationRegular } from '@fluentui/react-icons'
import { AppHeader } from './components/common/AppHeader'
import { AppFooter } from './components/common/AppFooter'
import { EffectsPanel } from './components/EffectsPanel'
import { ThemeSwitcher } from './components/ThemeSwitcher'
import type { SectionConfig } from './config/sections'
import { sectionsConfig } from './config/sections'
import { useIntersectionPreload } from './hooks/useIntersectionPreload'
import { useMobileDetection } from './hooks/useMobileDetection'
import { CvPrint } from './components/cv/CvPrint'
import { SkillsFilterProvider, useSkillsFilter } from './contexts/SkillsFilterContext'

// const BlogPage = React.lazy(() => import('./components/blog/BlogPage'))
// const ArticlePage = React.lazy(() => import('./components/blog/ArticlePage'))

function App() {
	// Create preload hooks for each section using direct imports
	const skillsMatrixPreload = useIntersectionPreload(() => import('./components/skills-matrix/SkillsMatrix'))
	const architecturePreload = useIntersectionPreload(() => import('./components/architecture/ArchitectureDiagram'))
	const careerTimelinePreload = useIntersectionPreload(() => import('./components/timeline/CareerTimeline'))
	const spfxShowcasePreload = useIntersectionPreload(() => import('./components/spfx-showcase/SpfxShowcase'))
	const teamsIntegrationPreload = useIntersectionPreload(() => import('./components/teams-integration/TeamsIntegration'))

	const getPreloadForSection = (key: string) => {
		switch (key) {
			case 'skillsMatrix': return skillsMatrixPreload
			case 'architecture': return architecturePreload
			case 'careerTimeline': return careerTimelinePreload
			case 'spfxShowcase': return spfxShowcasePreload
			case 'teamsIntegration': return teamsIntegrationPreload
			default: return null
		}
	}

	// const year = new Date().getFullYear()
	const navigate = useNavigate()
	const location = useLocation()
	const [isDrawerOpen, setIsDrawerOpen] = useState(false)
	const isMobile = useMobileDetection()
	const { matrixAutoOpen } = useSkillsFilter()

	// Dynamic sections state — all open by default; visitors scroll,
	// they don't click. Folding stays available per section.
	const [sectionsState, setSectionsState] = useState(
		sectionsConfig.reduce((acc: Record<string, boolean>, section: SectionConfig) => ({
			...acc,
			[section.key]: true
		}), {} as Record<string, boolean>)
	)

	const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
		navigate(data.value as string)
	}

	const onDrawerNav = (path: string) => {
		navigate(path)
		setIsDrawerOpen(false)
	}

	// Plain JSX (not an inline component) so App re-renders don't remount
	// the sections and replay their reveal animations / reset their state.
	const homeContent = (
		<>
			{sectionsConfig.map((section: SectionConfig) => {
				const preload = getPreloadForSection(section.key)
				const Component = section.component

				return (
					<div key={section.key} id={`section-${section.key}`} ref={preload?.ref}>
						<ScrollReveal>
							<FoldableSection
								icon={section.icon}
								title={section.title}
								description={section.description}
								isOpen={
									// hovering/tapping an architecture node opens the
									// skills matrix temporarily; the visitor's own
									// open/closed choice is preserved underneath
									section.key === 'skillsMatrix'
										? sectionsState[section.key] || matrixAutoOpen
										: sectionsState[section.key]
								}
								onToggle={() => setSectionsState((prev: Record<string, boolean>) => ({
									...prev,
									[section.key]: !prev[section.key]
								}))}
							>
								<Suspense fallback={<Spinner />}>
									<Component />
								</Suspense>
							</FoldableSection>
						</ScrollReveal>
					</div>
				)
			})}
		</>
	)

	return (
		<div className="App">
			<Drawer
				type="overlay"
				separator
				open={isDrawerOpen}
				onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
			>
				<DrawerHeader>
					<DrawerHeaderTitle
						action={
							<Button
								appearance="subtle"
								aria-label="Close"
								icon={<NavigationRegular />}
								onClick={() => setIsDrawerOpen(false)}
							/>
						}
					>
						Navigation
					</DrawerHeaderTitle>
				</DrawerHeader>
				<DrawerBody>
					<Button appearance="transparent" onClick={() => onDrawerNav('/')}>Home</Button>
					<Button appearance="transparent" onClick={() => onDrawerNav('/blog')}>Blog</Button>
				</DrawerBody>
			</Drawer>
			<ParticlesBackground />
			<div className="App-top-controls">
				<EffectsPanel />
				<ThemeSwitcher />
			</div>
			<AppHeader />
			<main id="content-start" className="App-bottom-area">
				<Routes>
					<Route path="/" element={homeContent} />
					{/* <Route path="/blog" element={<Suspense fallback={<Spinner />}><BlogPage /></Suspense>} /> */}
					{/*<Route path="/blog/:id" element={<Suspense fallback={<Spinner />}><ArticlePage /></Suspense>} /> */}
				</Routes>
			</main>
			<AppFooter
				isMobile={isMobile}
				location={location}
				onTabSelect={onTabSelect}
				onDrawerOpen={() => setIsDrawerOpen(true)}
			// year={year}
			/>
			<CvPrint />
		</div>
	)
}

const AppWrapper = () => (
	<Router>
		<SkillsFilterProvider>
			<App />
		</SkillsFilterProvider>
	</Router>
)

export default AppWrapper